import React, { FC, useCallback, useEffect, useRef } from "react";

interface IntersectionTriggerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Callback invoked when the element enters the viewport.
   */
  onIntersect: () => void;

  /**
   * Child elements to be wrapped by the trigger.
   */
  children?: React.ReactNode;

  /**
   * Margin around the root. Can have values similar to the CSS margin property (e.g. "50px 0px").
   * Increases or decreases the root area for intersection detection.
   * @default "100px"
   */
  rootMargin?: string;

  /**
   * Number(s) indicating at what percentage(s) of the target's visibility
   * the observer's callback should be executed. `0` means any part is visible,
   * `1` means fully visible.
   * @default 0
   */
  threshold?: number | number[];

  /**
   * If true, unobserve the element after the first intersection event.
   * @default false
   */
  triggerOnce?: boolean;
}

export const IntersectionTrigger: FC<IntersectionTriggerProps> = ({
  onIntersect,
  children,
  rootMargin = "100px",
  threshold = 0,
  triggerOnce = false,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onIntersect);

  // Keep the latest onIntersect in a ref so we don't need to re-init the observer on every render.
  useEffect(() => {
    callbackRef.current = onIntersect;
  }, [onIntersect]);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        callbackRef.current();
        if (triggerOnce) {
          observer.unobserve(entry.target);
        }
      }
    },
    [triggerOnce],
  );

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      callbackRef.current();
      return;
    }

    const observer: IntersectionObserver = new IntersectionObserver(
      (entries) => handleIntersect(entries, observer),
      {
        rootMargin,
        threshold,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, handleIntersect]);

  return (
    <div ref={ref} {...props}>
      {children}
    </div>
  );
};

export default IntersectionTrigger;
